import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { map, switchMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { CriteresRechercheStructure } from '../../models/criteres-recherche-structure.model';
import { environment } from '../../../environments/environment';
import { messageContentEnum } from '../../models/message-content-enum.model';
import { MessageService } from '../message/message.service';
import { ParametreContextuelDto } from '../../models/parametre-contextuel-dto.model';
import { RassService } from './rass.service';
import { StructureDto, StructureRassRecherche } from '../../models/structure-dto.model';
import { StructureRaisonIdNatPipe } from '../../shared/pipes/structure-raison-id-nat.pipe';
import { StructureTiers } from '../../models/structure.model';

@Injectable({
  providedIn: 'root',
})
export class StructureService {

  constructor(
    private readonly httpClient: HttpClient,
    private readonly rassService: RassService,
    private readonly structureRaisonIdNatPipe: StructureRaisonIdNatPipe,
    private readonly messageService: MessageService,
  ) { }

  /**
   * Méthode pour le service Rass structure/recherche qui permet de récupérer les strutures selon les critères de recherches.
   * Sont appliquées des filtres:
   *   - structure unique
   *   - structure tiers activée
   * @param criteres Parameters used to filter the results to search
   * @returns List of structure tiers activées
   */
  getEnabledStructureTiers(criteres: CriteresRechercheStructure): Observable<StructureRassRecherche[]> {
    let structuresRass;
    return this.rassService.getStructures(criteres).pipe(
      switchMap((x: Array<StructureRassRecherche>) => {
        structuresRass = x;
        const structures = x.map(s => this.structureRaisonIdNatPipe.transform(s, false));
        return this.httpClient.post(`${environment.API_URL}contrats/structure/getStructuresByIdNatStruct`, structures);
      }),
      tap(x => {
        if (Object.values(x).filter(s => s.structureTiers === StructureTiers.OUI).length === 0) {
          this.messageService.error(messageContentEnum.RASS_NO_RESULTS);
        }
      }),
      map(x => {
        return Object.values(x).filter(s => s.structureTiers === StructureTiers.OUI)
          .map(s => {
            const structureRass = structuresRass.find(structRass =>
              this.structureRaisonIdNatPipe.transform(structRass, false) === this.structureRaisonIdNatPipe.transform(s, false)
            );
            s.raisonSociale = structureRass.raisonSociale;
            s.commune = structureRass.commune;
            s.codePostal = structureRass.codePostal;
            return s;
          });
      }
      )
    );
  }

  getByListClePerimetreContractuel(list: string[]): Observable<Map<string, StructureDto>> {
    return !list || list.length === 0 ?
      new Observable((observer) => {
        observer.error('Pas de structure');
        observer.complete();
      })
      : this.httpClient.post<Map<string, StructureDto>>(
        `${environment.API_URL}contrats/structure/rechercherByStructurePerimetreContractuel`,
        list
      );
  }

  getByListIdStructure(list: string[]): Observable<Map<string, StructureDto>> {
    const body = {
      idStructures: list,
      isTest: false
    }
    return this.httpClient.post<Map<string, StructureDto>>(`${environment.API_URL}contrats/structure/rechercher`, body);
  }

  getListParametresContextuelByDomaine(domaine: string): Observable<Map<string, ParametreContextuelDto>> {
    const params = new HttpParams().set('domaine', domaine);
    return this.httpClient.get<Map<string, ParametreContextuelDto>>(
      `${environment.API_URL}contrats/parametreContextuel/rechercherByDomaine`,
      { params }
    );
  }

  getAllStructuresByNumeroContrat(numeroContrat: string): Observable<StructureDto[]> {
    const params = new HttpParams().set('numeroContrat', numeroContrat);
    return this.httpClient.get<StructureDto[]>(
      `${environment.API_URL}contrats/structure/getAllStructuresByNumeroContrat`,
      { params }
    );
  }

  detacherStructure(
    numeroContrat: string,
    idStructure: string,
    typeStructure: string,
    clePerimetreContractuel: string,
    mail: string,
    nom: string,
    prenom: string
  ): Observable<any> {
    return this.httpClient.post(
      `${environment.API_URL}cpx-habilitations/contrat/${numeroContrat}/detachementStructure/${typeStructure}/${idStructure}/${clePerimetreContractuel}`,
      { mail, nom, prenom },
      { observe: 'response' }
    );
  }

  homologateStructure(structure: StructureDto): Observable<any> {
    return this.httpClient.post(`${environment.API_URL}contrats/structure/homologateStructure`, structure);
  }
}
